import dayjs from "dayjs";
import { FC } from "react";
import { IOperatorInvoicePreview } from "../../interfaces/endpoints/IOperatorInvoice";
import Box from "../ui/Box";
import OperatorBreakdownTable from "./OperatorBreakdownTable";
import { CancellationsTable, ReservationRowsTable } from "./PreviewTables";

const formatMoney = (value: number) => `${(value ?? 0).toFixed(2)} EUR`;

const Totals: FC<{ label: string; value: string; accent?: boolean }> = ({
  label,
  value,
  accent
}) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase text-gray-500 mb-1">{label}</span>
    <span
      className={`text-base ${
        accent ? "font-bold text-primary" : "font-semibold"
      }`}
    >
      {value}
    </span>
  </div>
);

const PreviewSummary: FC<{ preview: IOperatorInvoicePreview }> = ({
  preview
}) => {
  const operators = preview.operators ?? [];
  // A combined preview spans >1 operator; otherwise behave exactly as before.
  const isCombined = operators.length > 1;
  const headerTitle = isCombined
    ? operators.map((o) => o.operatorName).join(" + ")
    : preview.operatorName;

  return (
    <Box className="mb-4">
      <div className="flex flex-col gap-1 mb-4">
        <h2 className="text-lg font-semibold">{headerTitle}</h2>
        <span className="text-sm text-gray-500">
          {dayjs.utc(preview.periodFrom).format("DD MMM YYYY")} —{" "}
          {dayjs.utc(preview.periodTo).format("DD MMM YYYY")}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b">
        <Totals label="Reservations" value={`${preview.reservationRowsCount}`} />
        <Totals
          label="Cancellations"
          value={`${preview.cancellationRowsCount}`}
        />
        <Totals label="Total price" value={formatMoney(preview.totalPrice)} />
        <Totals
          label="Gjirafa commission"
          value={formatMoney(preview.totalGjirafaCommission)}
        />
        <Totals
          label="Operator amount"
          value={formatMoney(preview.totalOperatorAmount)}
        />
        <Totals
          label="Cancellations total"
          value={formatMoney(preview.totalCancellations)}
        />
        <Totals
          label="Net billable"
          value={formatMoney(preview.netBillable)}
          accent
        />
      </div>

      {isCombined && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-2">Per operator</h3>
          <OperatorBreakdownTable operators={operators} />
        </div>
      )}

      {preview.items.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-2">Reservation rows</h3>
          <ReservationRowsTable
            items={preview.items}
            showOperator={isCombined}
          />
        </div>
      )}

      {preview.cancellations.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-2">Anulime</h3>
          <CancellationsTable
            cancellations={preview.cancellations}
            showOperator={isCombined}
          />
        </div>
      )}
    </Box>
  );
};

export default PreviewSummary;
